import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ApiCallService } from "../../../../api/service/api-call.service";
import * as Constants from "../../../../constant/Constants";
import { ValidationService } from "src/app/services/validation.service";
import { IConfirmDialogData } from "src/app/components/dialog/dialog.data";
import { ConfirmComponent } from "src/app/components/dialog/confirm/confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-dp-building-details",
  templateUrl: "./building-details.component.html",
  styleUrls: ["./building-details.component.css"],
})
export class DpBuildingDetailsComponent implements OnInit {
  token: string = "placeholder-value";
  loading: boolean = false;
  url: string = environment.baseUrl.replace("/motor","") + "/dp-chat"  + Constants.dpBuildingDetailsURL;

  buildingTypeOptions: string[] = ["Apartment/Flat", "Bungalow", "Town house/Villa","Maisonette"];

  genericDialogData: IConfirmDialogData = {
    title: "",
    content: "",
    cancelButtonText: "",
    confirmButtonText: "",
  };

  dpForm = this._formBuilder.group({
    buildingType: ["", Validators.required],
    numberOfStoreys: ["", [Validators.required, Validators.min(1), Validators.max(100)]],
    lrNumber: ["", Validators.required],
    streetAddress: ["", Validators.required],
    gpsCoordinates: [""],
  });

  formErrors: { [key: string]: string } = {
    buildingType: "",
    numberOfStoreys: "",
    lrNumber: "",
    streetAddress: "",
    gpsCoordinates: "",
  };

  validationMessages: { [key: string]: { [key: string]: string } } = {
    buildingType: { required: "This field is required" },
    numberOfStoreys: { required: "This field is required", min: "Must be at least 1", max: "Invalid value" },
    lrNumber: { required: "This field is required" },
    streetAddress: { required: "This field is required" },
  };

  constructor(
    private route: ActivatedRoute,
    private apiCallService: ApiCallService,
    private _formBuilder: FormBuilder,
    private validationService: ValidationService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params["token"] && params["token"] !== "undefined") {
        this.token = params["token"];
        this.loadPrefill();
      }
    });

    this.dpForm.valueChanges.subscribe(() => {
      this.logErrors();
    });

    this.dpForm.get("numberOfStoreys")?.valueChanges.subscribe((value) => {
      if (value) {
        const raw = value.replace(/\D/g, "").replace(/^0+/, "");
        if (value !== raw) {
          this.dpForm.get("numberOfStoreys")?.setValue(raw, { emitEvent: false });
        }
      }
    });
  }

  async loadPrefill(): Promise<void> {
    try {
      const response = await this.apiCallService.getRequest(`${this.url}?token=${this.token}`);
      if (response && response.error === false && response.prefill) {
        const p = response.prefill;
        this.dpForm.patchValue({
          buildingType:    p.buildingType    || "",
          numberOfStoreys: p.numberOfStoreys || "",
          lrNumber:        p.lrNumber        || "",
          streetAddress:   p.streetAddress   || "",
          gpsCoordinates:  p.gpsCoordinates  || "",
        });
      }
    } catch (error) {
      console.error("Failed to load prefill data", error);
    }
  }

  logErrors(): void {
    this.validationService.logValidationErrors(this.dpForm, this.formErrors, this.validationMessages);
  }

  submitForm(): void {
    this.initDialogData(
      "Confirm submission",
      "Please proceed only if you have verified that the details are correct",
      "Back",
      "Proceed"
    );
    this.showGenericPopup(this.postDataToWhatsapp);
  }

  postDataToWhatsapp = async () => {
    this.loading = true;
    const f = this.dpForm.value;

    const req = {
      buildingType: f.buildingType,
      numberOfStoreys: f.numberOfStoreys,
      lrNumber: f.lrNumber,
      streetAddress: f.streetAddress,
      gpsCoordinates: f.gpsCoordinates,
      token: this.token,
      filled: true,
    };

    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = response.nextUrl ?? environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Submission Failed",
          "An error occurred while saving the form details",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.postDataToWhatsapp);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      this.initDialogData(
        "Submission Failed",
        "An unexpected error occurred",
        "Cancel",
        "TRY AGAIN"
      );
      this.showGenericPopup(this.postDataToWhatsapp);
    }
  };

  confirmCancel(): void {
    this.initDialogData(
      "Warning",
      "Are you sure you want to cancel the application?",
      "back",
      "Cancel application"
    );
    this.showGenericPopup(this.cancelApplication);
  }

  cancelApplication = async () => {
    this.loading = true;
    const req = { token: this.token, filled: false };
    try {
      const response = await this.apiCallService.postRequest(
        `${this.url}?token=${this.token}`,
        req
      );
      if (response.error === false) {
        window.location.href = environment.whatsappRedirectURL;
      } else {
        this.loading = false;
        this.initDialogData(
          "Failed",
          "An error occurred while attempting to cancel",
          "Cancel",
          "TRY AGAIN"
        );
        this.showGenericPopup(this.cancelApplication);
      }
    } catch (error) {
      console.error(error);
      this.loading = false;
      window.location.href = environment.whatsappRedirectURL;
    }
  };

  showGenericPopup(runFunction: () => void): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      disableClose: true,
      data: this.genericDialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        runFunction();
      }
    });
  }

  initDialogData(
    title: string,
    content: string,
    cancelButtonText: string,
    confirmButtonText: string
  ): void {
    this.genericDialogData.title = title;
    this.genericDialogData.content = content;
    this.genericDialogData.cancelButtonText = cancelButtonText;
    this.genericDialogData.confirmButtonText = confirmButtonText;
  }
}
